import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { useAuth } from '../../../context/AuthContext';
import { useFavorites } from '../../../lib/favorites';
import { groupByCategory, matchesTool, joinRecent } from './toolsModel';
import ToolTile from './ToolTile';
import RecentTools from './RecentTools';
import PeopleResults from './PeopleResults';

export default function ToolsPanel() {
  const { user } = useAuth();
  const { favoriteIds, toggleFavorite } = useFavorites(user?.email);
  const [tools, setTools] = useState([]);
  const [categoryIcons, setCategoryIcons] = useState({});
  const [recentRows, setRecentRows] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [reloadKey, setReloadKey] = useState(0);
  const [term, setTerm] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    const opts = { withCredentials: true };
    // Categories (icons) and recent are decorative: their failure never
    // becomes a page error.
    axios.get(`${API_BASE_URL}/api/categories`, opts)
      .then((res) => {
        if (!active) return;
        const icons = {};
        (res.data || []).forEach((c) => { icons[c.name] = c.icon; });
        setCategoryIcons(icons);
      })
      .catch(() => {});
    axios.get(`${API_BASE_URL}/api/my/recent-tools`, opts)
      .then((res) => { if (active) setRecentRows(Array.isArray(res.data?.tools) ? res.data.tools : []); })
      .catch(() => { if (active) setRecentRows([]); });
    axios.get(`${API_BASE_URL}/api/my/tools`, opts)
      .then((res) => { if (active) { setTools(Array.isArray(res.data) ? res.data : []); setStatus('ready'); } })
      .catch(() => { if (active) { setTools([]); setStatus('error'); } });
    return () => { active = false; };
  }, [reloadKey]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (e.key !== '/' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const searching = term.trim().length > 0;
  const visible = useMemo(() => tools.filter((t) => matchesTool(t, term)), [tools, term]);
  const categories = useMemo(() => groupByCategory(visible), [visible]);
  // Filtering against the loaded catalog drops stale ids for revoked tools.
  const favorites = tools.filter((t) => favoriteIds.includes(t.id));
  const recent = useMemo(() => joinRecent(recentRows, tools), [recentRows, tools]);

  const tile = (t) => (
    <ToolTile key={t.id} tool={t} isFavorite={favoriteIds.includes(t.id)} onToggleFavorite={toggleFavorite} />
  );

  return (
    <section className="home-tools" aria-label="Your tools">
      <div className="home-search">
        <label className="sr-only" htmlFor="home-search">Search tools and people</label>
        <svg className="home-search__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          id="home-search"
          ref={searchRef}
          type="search"
          className="search-input"
          placeholder="Search tools and people"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          autoComplete="off"
        />
        <kbd className="home-search__kbd" aria-hidden="true">/</kbd>
      </div>

      {status === 'loading' && (
        <div className="state-panel" role="status" aria-busy="true">
          <div className="spinner" />
          <span className="sr-only">Loading your tools…</span>
        </div>
      )}

      {status === 'error' && (
        <div className="state-panel" role="alert">
          <h3 className="state-panel__title">Couldn’t load your tools</h3>
          <p className="state-panel__body">Something went wrong reaching the server. Please try again in a moment.</p>
          <button type="button" className="btn btn-secondary" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
        </div>
      )}

      {status === 'ready' && tools.length === 0 && (
        <div className="state-panel">
          <h3 className="state-panel__title">No tools yet</h3>
          <p className="state-panel__body">You don’t have access to any tools right now. Request access to the tools you need and they’ll show up here.</p>
          <Link to="/requests#sub=access" className="btn btn-primary">Request a tool</Link>
        </div>
      )}

      {status === 'ready' && tools.length > 0 && (
        <>
          {!searching && (
            <section className="home-section" aria-labelledby="home-fav-h">
              <h2 id="home-fav-h" className="home-section__title">Favorites</h2>
              {favorites.length > 0
                ? <div className="favorite-apps-grid">{favorites.map(tile)}</div>
                : <p className="home-muted">Star a tool to pin it here.</p>}
            </section>
          )}

          {!searching && <RecentTools items={recent} />}

          <section className="home-section" aria-labelledby="home-tools-h">
            <div className="home-section__head">
              <h2 id="home-tools-h" className="home-section__title">{searching ? 'Matching tools' : 'All my tools'}</h2>
              <span className="home-muted">{visible.length} {visible.length === 1 ? 'tool' : 'tools'}</span>
            </div>
            {visible.length === 0 && <p className="home-muted">No tools match “{term.trim()}”.</p>}
            {searching ? (
              // One flat grid while searching: the matches are the point, and
              // category headers would split a short result list into scraps.
              visible.length > 0 && <div className="home-results-grid">{visible.map(tile)}</div>
            ) : (
            <div className="categories-grid">
            {categories.map((cat) => (
              <div className="category-section" key={cat.name}>
                <h3 className="category-title">
                  {categoryIcons[cat.name] && (
                    // Icon SVG is sanitised on write by sanitizeCategoryIcon (routes/categories.js),
                    // rendered exactly as Dashboard does.
                    <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: categoryIcons[cat.name] }} />
                  )}
                  {cat.name}
                </h3>
                <div className="apps-grid">{cat.tools.map(tile)}</div>
              </div>
            ))}
            </div>
            )}
          </section>
        </>
      )}

      {/* Outside the tools states on purpose: someone with no tools yet, or
          whose tools failed to load, can still find a colleague. */}
      {searching && <PeopleResults term={term} />}
    </section>
  );
}
